import { Download, Maximize2 } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  Button,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  downloadRowsCsv,
  downloadRowsXlsx,
  tableExportFilename,
} from '@/components/ui'
import type { ColumnDef } from '@/components/ui/table/types'
import { cn } from '@/utils'
import ExpandedArtifactDialog from './ExpandedArtifactDialog'

export interface TableColumn {
  key: string
  label: string
  format?: 'currency' | 'date' | 'number' | 'text' | null
}

export interface TableSpec {
  title?: string | null
  columns: TableColumn[]
  rows: Array<Record<string, unknown>>
  total_row?: Record<string, unknown> | null
  receipt?: string | null
}

interface TableArtifactProps {
  spec: TableSpec
}

const currencyFormatter = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
})

export function formatCell(
  value: unknown,
  format?: TableColumn['format'],
): string {
  if (value === null || value === undefined) return ''
  if (format === 'currency' && typeof value === 'number') {
    return currencyFormatter.format(value)
  }
  if (format === 'number' && typeof value === 'number') {
    return value.toLocaleString()
  }
  return String(value)
}

export function isNumeric(format?: TableColumn['format']): boolean {
  return format === 'currency' || format === 'number'
}

// Row shape DataTable can identify. Aggregate rows have no natural id,
// so we synthesize one from the row index.
type AggregateRow = Record<string, unknown> & { id: string }

export default function TableArtifact({ spec }: TableArtifactProps) {
  const isEmpty = !spec.rows || spec.rows.length === 0
  const baseFilename = useMemo(
    () => tableExportFilename(spec.title),
    [spec.title],
  )
  const [expanded, setExpanded] = useState(false)

  const data = useMemo<AggregateRow[]>(
    () =>
      spec.rows.map((row, index) => ({
        ...row,
        id: `row-${index}`,
      })),
    [spec.rows],
  )

  const columns = useMemo(
    () => buildColumns(spec.columns, true),
    [spec.columns],
  )

  const expandedColumns = useMemo(
    () => buildColumns(spec.columns, false),
    [spec.columns],
  )

  const totalRow = spec.total_row ? <TotalRow spec={spec} /> : null
  const receipt = spec.receipt ? (
    <p className="border-t px-4 py-2 text-xs text-muted-foreground">
      {spec.receipt}
    </p>
  ) : null

  return (
    <>
      <figure
        className={cn(
          'my-1 w-full overflow-hidden rounded-lg border bg-card',
          TOP_ALIGNED_ROWS,
        )}
      >
        {(spec.title || !isEmpty) && (
          <figcaption className="flex items-center justify-between gap-3 border-b px-4 py-2.5 text-sm font-medium text-foreground">
            <span className="line-clamp-2">{spec.title ?? ''}</span>
            {!isEmpty && (
              <span className="flex shrink-0 gap-0.5">
                <Button
                  type="button"
                  theme="ghost"
                  size="xs"
                  isIcon
                  onClick={() => setExpanded(true)}
                  title="Expand"
                  aria-label="Expand"
                >
                  <Maximize2 className="size-3.5" aria-hidden />
                </Button>
                <TableDownloadMenu spec={spec} baseFilename={baseFilename} />
              </span>
            )}
          </figcaption>
        )}
        {isEmpty ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            No data to display.
          </div>
        ) : expanded ? (
          // Drop the inline DataTable while the dialog owns one — two
          // tanstack/virtualizer instances mounted at once is the main
          // expanded-mode cost we're trimming.
          <div className="flex h-80 items-center justify-center px-4 text-sm text-muted-foreground">
            Showing in the expanded view — close to view here.
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={data}
            height="h-auto max-h-80"
            enableRowSelection={false}
            stickyFooter={totalRow}
          />
        )}
        {receipt}
      </figure>

      <ExpandedArtifactDialog
        open={expanded}
        onOpenChange={setExpanded}
        title={spec.title ?? 'Table'}
        headerActions={
          isEmpty ? null : (
            <TableDownloadMenu spec={spec} baseFilename={baseFilename} />
          )
        }
      >
        <div
          className={cn(
            'flex flex-1 flex-col overflow-hidden',
            TOP_ALIGNED_ROWS,
          )}
        >
          <DataTable
            columns={expandedColumns}
            data={data}
            height="flex-1"
            enableRowSelection={false}
            stickyFooter={totalRow}
          />
        </div>
        {receipt}
      </ExpandedArtifactDialog>
    </>
  )
}

// Build tanstack column defs from the wire TableColumn[]. We do the
// formatting in the cell renderer rather than passing pre-formatted
// strings as data so DataTable's sorting (when available) gets the raw
// values. Text columns flex to fill the width and wrap long values;
// numbers keep a fixed width and never wrap. Inline, text is clamped so
// one long cell cannot fill the whole box; hover shows the full value.
function buildColumns(
  specColumns: TableColumn[],
  clampText: boolean,
): ColumnDef<AggregateRow, unknown>[] {
  return specColumns.map((column) => {
    const numeric = isNumeric(column.format)
    const def: ColumnDef<AggregateRow, unknown> = {
      accessorKey: column.key,
      header: column.label,
      size: columnSize(column),
      enableResizing: false,
      meta: numeric ? { wrap: true } : { wrap: true, isFluid: true },
      cell: ({ getValue }) => {
        const formatted = formatCell(getValue(), column.format)
        if (numeric) {
          return (
            <div className="w-full text-right tabular-nums whitespace-nowrap">
              {formatted}
            </div>
          )
        }
        return (
          <div
            className={cn('min-w-0', clampText && 'line-clamp-3')}
            title={clampText ? formatted : undefined}
          >
            {formatted}
          </div>
        )
      },
    }
    return def
  })
}

function columnSize(column: TableColumn): number {
  return isNumeric(column.format) ? 140 : 180
}

// A tall cell scrolled out of view must not push the visible text down.
const TOP_ALIGNED_ROWS = '[&_[data-index]>div:first-child]:items-start'

// The total is a sticky footer, not a data row, so sorting never moves it.
function TotalRow({ spec }: { spec: TableSpec }) {
  return (
    <div className="flex w-(--table-width) bg-foreground/[0.05] px-4 py-2 text-sm font-medium">
      {spec.columns.map((column) => {
        const numeric = isNumeric(column.format)
        return (
          <div
            key={column.key}
            className={cn(
              'px-2 first:pl-0',
              numeric
                ? 'shrink-0 text-right tabular-nums'
                : 'min-w-0 flex-1 break-words',
            )}
            style={numeric ? { width: columnSize(column) } : undefined}
          >
            {formatCell(spec.total_row?.[column.key], column.format)}
          </div>
        )
      })}
    </div>
  )
}

interface TableDownloadMenuProps {
  spec: TableSpec
  baseFilename: string
}

function TableDownloadMenu({ spec, baseFilename }: TableDownloadMenuProps) {
  const table = {
    columns: spec.columns,
    rows: spec.rows,
    totalRow: spec.total_row,
    title: spec.title,
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          theme="ghost"
          size="xs"
          isIcon
          title="Download"
          aria-label="Download"
        >
          <Download className="size-3.5" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={() => downloadRowsCsv(table, `${baseFilename}.csv`)}
        >
          Download as CSV
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => downloadRowsXlsx(table, `${baseFilename}.xlsx`)}
        >
          Download as XLSX
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
